import { PurhcaseRequestDetails } from "../models/PurchaseRequest/PurchaseRequestDetails";

export const PO_TERMINAL_STATUSES = ["CANCELLED", "CLOSED"] as const;

/** PO statuses that block Generate/Regenerate PO. */
export const PO_ACTIVE_STATUSES = ["CREATED", "OPEN", "CLOSED"] as const;

export function isPoCancelledOrClosed(status?: string | null): boolean {
  if (!status) return false;
  return (PO_TERMINAL_STATUSES as readonly string[]).includes(status);
}

export function isPoCancelled(status?: string | null): boolean {
  return (status || "").toUpperCase() === "CANCELLED";
}

/** True when any PO is Created, Open, or Closed (blocks Generate/Regenerate). */
export function hasActivePurchaseOrdersBlockingGenerate(
  detail: PurhcaseRequestDetails | null | undefined,
): boolean {
  return (detail?.purchase_orders ?? []).some((po) =>
    (PO_ACTIVE_STATUSES as readonly string[]).includes(
      (po.status || "").toUpperCase(),
    ),
  );
}

/** Generate/Regenerate allowed when there are no POs, or only Cancelled POs. */
export function canGenerateOrRegeneratePurchaseOrder(
  detail: PurhcaseRequestDetails | null | undefined,
): boolean {
  if (!detail) return false;
  const orders = detail.purchase_orders ?? [];
  if (orders.length === 0) return true;
  return orders.every((po) => isPoCancelled(po.status));
}

/** True when a PO is still open (CREATED/OPEN), using purchase_orders when active_po is absent. */
function hasActivePurchaseOrder(detail: PurhcaseRequestDetails): boolean {
  if (Number(detail.active_po) === 1) return true;
  return (detail.purchase_orders ?? []).some(
    (po) => po.status === "CREATED" || po.status === "OPEN",
  );
}

export function getPrLinkedPurchaseOrder(
  detail: PurhcaseRequestDetails | null | undefined,
) {
  const orders = detail?.purchase_orders ?? [];
  if (orders.length === 0) return undefined;

  if (detail?.po_id && detail.po_id > 0) {
    const byPoId = orders.find((po) => po.id === detail.po_id);
    if (byPoId) return byPoId;
  }

  return (
    orders.find((po) => isPoCancelledOrClosed(po.status)) ??
    orders[orders.length - 1]
  );
}

export function getResolvedPoId(
  detail: PurhcaseRequestDetails,
): number | undefined {
  return getPrLinkedPurchaseOrder(detail)?.id ?? undefined;
}

/** Edit allowed before first PO, or after PO cancel/close with permission. */
export function canEditPrProductFields(
  detail: PurhcaseRequestDetails,
  hasPostPoEditPermission: boolean,
): boolean {
  if (detail.pr_cancellation_date || detail.active === 0) return false;
  if (!detail.purchase_orders || detail.purchase_orders.length === 0) return true;
  if (!hasPostPoEditPermission) return false;
  if (hasActivePurchaseOrder(detail)) return false;
  const linkedPo = getPrLinkedPurchaseOrder(detail);
  return isPoCancelledOrClosed(linkedPo?.status);
}

/**
 * True when the PR is still awaiting both sourcing and finance approval
 * (neither approved, not cancelled, not rejected). Used for catalog
 * sourcing field edits (e.g. HSN) via PATCH /products/:id/sourcing.
 */
export function isPrPendingSourcingAndFinanceApproval(
  detail: PurhcaseRequestDetails | null | undefined,
): boolean {
  if (!detail) return false;
  if (detail.pr_cancellation_date || detail.active === 0) return false;

  const financeRejected =
    (Number(detail.rejected_by_finance_admin_id) || 0) > 0;
  const sourcingRejected =
    (Number(detail.rejected_by_sourcing_admin_id) || 0) > 0;
  const swageazyRejected =
    (Number(detail.rejected_by_swageazy_admin_id) || 0) > 0;
  if (financeRejected || sourcingRejected || swageazyRejected) return false;

  return (
    Boolean(detail.sourcing_approval) === false &&
    Boolean(detail.finance_approval) === false
  );
}

export function canReactivatePurchaseOrder(
  detail: PurhcaseRequestDetails,
  hasPostPoEditPermission: boolean,
): boolean {
  if (!canEditPrProductFields(detail, hasPostPoEditPermission)) return false;
  const linkedPo = getPrLinkedPurchaseOrder(detail);
  return Boolean(linkedPo && isPoCancelledOrClosed(linkedPo.status));
}
